import { useTheme } from "@emotion/react";
import { CcRootContext } from "../../conversionCenterRoot";
import React, { useState, useContext, useEffect, useRef } from "react";
import { DateTime } from "luxon";
import "flatpickr/dist/themes/airbnb.css";
import { useForm } from "react-hook-form";
import Calendly from "./calendar/calendly";
import CalendarPage from "./calendar/calendarPage";
import CcInput from "../ccInput";
import CcButton from "../ccButton";
import { getArrayLastItem } from "../../helpers/helpers";
import { EMAIL_REGEXP } from "../../../../utils/constants";

export const Calendar = ({ data, defaultScreen }) => {
  const today = new Date();
  const ctx = useContext(CcRootContext);
  const current = defaultScreen || getArrayLastItem(ctx.history);
  const theme = useTheme();
  const formRef = useRef(null);
  const [page, setPage] = useState(0);
  const [dayDate, setDayDate] = useState(today);
  const [selectedTime, setSelectedTime] = useState("");
  const { register, handleSubmit, errors, reset } = useForm();
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);
  const [locked, setLocked] = useState(false);
  
  const onSubmit = (data) => {
    setLocked(true);
    const contactUuid = ctx.sites
      .filter((site) => site.specialtyName === current.activeSpecialty)[0].elements
      .filter((element) => element.type === "Contact")[0].contactUuid;

    const formData = {
      contact_uuid: contactUuid,
      email: data.email,
      message: `${data.name} scheduled an appointment for ${DateTime.fromISO(new Date(data.date).toISOString()).toLocaleString(DateTime.DATE_FULL)} at ${data.time}`,
      details: {
        phone: data.phone,
        name: data.name,
      },
    };

    fetch("/api/public/contact_requests/", {
      method: "POST",
      headers: {
        "accept": "*/*",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
      },
      body: JSON.stringify({
        ...formData,
        contact_request: formData,
      }),
    })
      .then((resp) => {
        if(resp.ok) {
          setShowSuccessMsg(true);
          formRef.current.querySelectorAll('label').forEach((label) => {
            label.classList.remove("focused");
          });
          setPage(0);
          setSelectedTime("");
          reset();
        }
        setLocked(false);
      })
      .catch((error) => {
        setLocked(false);
        console.error(error);
      });
  };

  useEffect(() => {
    if (!showSuccessMsg) return;
    setTimeout(() => {
      setShowSuccessMsg(false);
    }, 8000);
  }, [showSuccessMsg]);

  const headingStyle = {
    fontFamily: theme.font.primary,
    margin: "9px 0 7px",
  };

  const subHeadingStyle = {
    fontFamily: theme.font.primary,
    margin: 0,
    marginBottom: 30,
  };

  const summaryStyle = {
    fontSize: theme.f_sizes.l,
    fontWeight: 500,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    "span": {
      display: "inline-block",
      marginRight: "auto",
    },
  };

  const successMsgStyle = {
    color: theme.colors.success,
    fontWeight: 500,
    textAlign: "center",
  };

  return (
    <>
      <h2 css={headingStyle}>{data?.title ||"Schedule an appointment"}</h2>
      <p css={subHeadingStyle}>Select date and time</p>
      {showSuccessMsg && (
        <p css={successMsgStyle}>Your appointment request have been submitted. We will contact you soon to confirm the date.</p>
      )}
      {data?.useCalendly && data.calendlyLink ? (
        <Calendly url={data.calendlyLink} />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
          ref={formRef}
        >
          <input type="hidden" name="date" ref={register} value={dayDate}/>
          <input type="hidden" name="time" ref={register} value={selectedTime}/>
          {page === 0 && (
            <CalendarPage 
              data={data}
              dayDate={dayDate}
              setDayDate={setDayDate}
              selectedTime={selectedTime}
              setSelectedTime={setSelectedTime}
              setPage={setPage}
              summaryStyle={summaryStyle}
            />
          )}
          {page === 1 && (
            <>
              <div css={summaryStyle}>
                <span>{DateTime.fromISO(new Date(dayDate).toISOString()).toLocaleString(DateTime.DATE_FULL)} {selectedTime}</span>
                {selectedTime && (
                  <CcButton
                    onClick={() => setPage(0)}
                    label="Change date"
                    fullWidth={false}
                  />
                )}
              </div>
              <div css={{ padding: "20px 36px 0" }}>
                <CcInput
                  defaultValue=""
                  id="name"
                  name="name"
                  register={register({ required: `Name cannot be empty` })}
                  error={errors?.name}
                />
                <CcInput
                  defaultValue=""
                  id="phone"
                  name="phone"
                  register={register({ required: `Phone cannot be empty` })}
                  error={errors?.phone}
                />
                <CcInput
                  defaultValue=""
                  id="email"
                  name="email"
                  register={register({
                    required: `Email cannot be empty`,
                    pattern: {
                      value: EMAIL_REGEXP,
                      message: "Email is in wrong format",
                    }
                  })}
                  error={errors?.email}
                />
                <CcButton
                  type="submit"
                  label="Submit"
                  disabled={locked}
                  isActive={true}
                />
              </div>
            </>
          )}
        </form>
      )}
    </>
  );
};
